import type { JsonObject } from "../wire/json";
import type { FrameReport, WireTool } from "../wire/protocol";
import type { BridgeDispatcher } from "./dispatcher";
import type { ProjectIdentity } from "./projectIdentity";
import type { SocketDrop } from "../wire/protocol";
import type { SocketLike } from "./relaySocket";
import type { SessionPush, VerifiedIdentity } from "./relaySocket";
import { createRelaySocket } from "./relaySocket";

/**
 * The app frame's end of the bridge: build this frame's descriptor, hold a
 * socket to the relay, and route calls into the dispatcher.
 *
 * ## What #22 changed here
 *
 * This file used to embed a hidden BROKER iframe and speak `postMessage` to it,
 * because the broker's constant origin was the one place a pairing credential
 * could be written once and found by the next app on another domain. There is
 * no stored credential — every frame proves the user itself — and each app
 * already got its own broker document and its own socket, so the shared origin
 * was sharing nothing.
 *
 * ⚠️ **The token no longer crosses an origin boundary at all.** It went app
 * frame → broker → relay, which this file's previous comment called a real if
 * temporary exposure. It now goes straight to the relay over the socket.
 *
 * Everything that can be tested without a DOM lives in `relaySocket.ts`, which
 * touches no global. This file is the thin adapter that supplies `WebSocket`,
 * `window.setTimeout` and this frame's origin — so what is untested here is
 * only the wiring, not the reconnect or account-switch logic.
 */

/**
 * What the RELAY says is live, as this frame sees it — the fix for #10.
 *
 * `activeHere` and `controlHere` are the only fields derived rather than
 * reported, and both are derived from a value this frame already owns: the
 * project id it registered with. That keeps the relay authoritative about
 * *what* is active while letting the frame answer *is it me* locally.
 */
export type SessionView = SessionPush & {
  /** True when the active project is the one THIS frame registered into. */
  readonly activeHere: boolean;
  /**
   * True when this frame's project has the MCP control app open. A carrier
   * with its switch on and this false tells the user to open the control app.
   */
  readonly controlHere: boolean;
};

export type BridgeStatus = {
  readonly connected: boolean;
  /**
   * The VERIFIED Giraffe user this socket belongs to, as the relay decided it,
   * with the names the same token carries.
   *
   * A DERIVED FACT, not a credential — the token never comes back down — so a
   * real app panel may print it, which was not true of the install id it
   * replaced. ⚠️ Null is "the relay has not said yet", never "nobody".
   */
  readonly identity: VerifiedIdentity | null;
  /**
   * Null until the relay has said. **Null is not "inactive"** — it is "not yet
   * told", and a panel that renders the two the same way re-creates #10: it
   * would state an access level it has no basis for.
   */
  readonly session: SessionView | null;
  /**
   * Why this frame is not connected, in one sentence, or null.
   *
   * It exists because obtaining the token can fail — no host on the other end
   * of `postMessage`, an SDK that moved, an expired session. A rejection with
   * nowhere to go leaves the panel connecting forever with nothing anywhere
   * saying why, which is the failure `docs/tool-surface.md`'s status vocabulary exists to prevent.
   */
  readonly error: string | null;
  /**
   * Why the socket is down, or null while it is up. See `SocketDrop`: a
   * refusal, a socket that never opened and a drop after opening each render
   * differently, because the person looking at the panel is the only one who
   * can see which it was.
   */
  readonly drop: SocketDrop | null;
  /**
   * True once the relay has refused this frame several times in a row and it
   * has stopped dialling. Nothing reconnects it on its own: `retry`, or a new
   * bridge, is the only way back.
   */
  readonly stopped: boolean;
  /**
   * True while the bridge has let go of its socket because nothing it could do
   * matters: its tab is hidden and another project is active (`presence.ts`).
   * Not a failure — `drop` is null — and it dials again when the tab returns.
   */
  readonly dormant: boolean;
};

export type BridgeAttachment = {
  /** The control app's tab came to the front (`docs/session-model.md`). */
  readonly front: () => void;
  readonly detach: () => void;
  /** Dial again after the frame stopped on repeated refusals. Does nothing otherwise. */
  readonly retry: () => void;
  /** Who the relay says this frame is, or null before it has answered. */
  readonly userId: () => string | null;
  /**
   * The app moved. A new project re-dials with a fresh token, because the relay
   * admits a frame only for the project its token names; anything else
   * re-reports on the same socket. `activeHere` follows the new project.
   * Rejects when the re-report could not be made, so the caller can try again.
   */
  readonly relocate: (location: ProjectIdentity) => Promise<void>;
};

export type BridgeConfig = {
  /** The relay's socket endpoint. Dialled DIRECTLY as of #22 — no broker. */
  readonly relayUrl: string;
  readonly frameId: string;
  readonly app: string;
  readonly workspaceId: string;
  readonly projectId: string;
  readonly projectUrl: string;
  readonly dispatcher: BridgeDispatcher;
  /**
   * The frame's own credential, re-derived PER DIAL rather than stored — see
   * `relaySocket.ts`, which now calls this on every dial including a backoff
   * re-dial rather than only on registration.
   */
  readonly getToken: () => Promise<string>;
  readonly onStatus?: (status: BridgeStatus) => void;
  /**
   * The relay's `POST /report` URL, or absent to report nothing. See
   * `DropReport` in `wire/protocol.ts` for why a drop travels over HTTPS
   * rather than the socket that just failed.
   */
  readonly reportUrl?: string;
};

export function attachBridge(config: BridgeConfig): BridgeAttachment {
  let identity: VerifiedIdentity | null = null;
  let connected = false;
  let session: SessionView | null = null;
  let error: string | null = null;
  let drop: SocketDrop | null = null;
  let stopped = false;

  const announce = (): void => {
    config.onStatus?.({ connected, identity, session, error, drop, stopped, dormant: false });
  };

  /**
   * ⚠️ No `appOrigin`. This frame does not get to state its own origin — the
   * relay reads it off the WebSocket handshake, where the browser stamps it and
   * page script cannot. Sending one here would be a field the relay must know
   * to ignore, which is how the old self-reported value survived as long as it
   * did.
   */
  let report: FrameReport = {
    frameId: config.frameId,
    app: config.app,
    workspaceId: config.workspaceId,
    projectId: config.projectId,
    projectUrl: config.projectUrl,
    tools: config.dispatcher.toolList() as WireTool[],
  };

  const socket = createRelaySocket({
    relayUrl: config.relayUrl,
    getToken: config.getToken,
    openSocket: (url: string): SocketLike => new WebSocket(url),
    schedule: (fn: () => void, delayMs: number): void => {
      window.setTimeout(fn, delayMs);
    },
    scheduleHeartbeat: (fn: () => void, delayMs: number): void => {
      window.setTimeout(fn, delayMs);
    },
    onReady: (
      isConnected: boolean,
      verified: VerifiedIdentity | null,
      why: SocketDrop | null,
      gaveUp: boolean,
    ): void => {
      connected = isConnected;
      identity = verified;
      drop = why;
      stopped = gaveUp;
      // A connect clears the last failure: it reports the attempt that produced
      // it, not a property of this frame.
      if (isConnected) error = null;
      // A socket that has gone takes the relay's session view with it. Holding
      // the last one would leave the panel asserting an access level nothing
      // is currently serving — stale in the unsafe direction.
      if (!isConnected) session = null;
      announce();
    },
    report: (report): void => {
      const url = config.reportUrl;
      if (url === undefined) return;
      // `text/plain`, deliberately: it is a CORS-safelisted type, so the
      // browser sends ONE request rather than a preflight and then the report —
      // and a network that is already dropping things gets one chance to drop
      // this, not two. `keepalive` lets a report outlive a panel being closed.
      void fetch(url, {
        method: "POST",
        headers: { "content-type": "text/plain" },
        body: JSON.stringify(report),
        keepalive: true,
      }).catch((cause: unknown) => {
        // Nowhere further to send it: the report IS the fallback channel.
        console.log({
          event: "bridge.report_failed",
          message: cause instanceof Error ? cause.message : String(cause),
        });
      });
    },
    onCall: (call): void => {
      void config.dispatcher.handle(call.tool, call.args as JsonObject).then((result) => {
        socket.result(call.id, result as never);
      });
    },
    onSession: (push): void => {
      session = {
        ...push,
        activeHere: push.activeProjectId === report.projectId,
        controlHere: push.controlProjectIds.includes(report.projectId),
      };
      announce();
    },
  });

  /**
   * Registration is ASYNC, so its rejection is handled HERE and LOUDLY.
   *
   * The token is derived per dial rather than stored, so obtaining it can fail.
   * `noFloatingPromises` would flag a bare call, and a dropped one would leave
   * the panel connecting forever with nothing saying why — the failure `docs/tool-surface.md`'s
   * status vocabulary exists to prevent.
   */
  const failedToDial = (cause: unknown): void => {
    error = cause instanceof Error ? cause.message : String(cause);
    connected = false;
    announce();
  };
  const register = (): void => {
    socket.register(report).catch(failedToDial);
  };
  register();

  return {
    front: () => {
      socket.front();
    },
    detach: () => {
      socket.close();
    },
    retry: () => {
      socket.retry().catch(failedToDial);
    },
    userId: () => identity?.userId ?? null,
    relocate: async (location) => {
      report = { ...report, ...location };
      try {
        await socket.register(report);
      } catch (cause) {
        // The socket is still up and still serving the last frame it reported,
        // so `connected` stays as it is: only the re-report failed.
        error = cause instanceof Error ? cause.message : String(cause);
        announce();
        throw cause;
      }
      if (error !== null) {
        error = null;
        announce();
      }
    },
  };
}
